import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  ChevronLeft,
  CloudOff,
  EllipsisVertical,
  Search,
  Download,
  Users,
} from "lucide-react";
import { Button } from "@/client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/client/components/ui/tooltip";
import { useOnline } from "@/client/hooks/use-document";
import { ShortcutsDialog } from "./shortcuts-dialog";
import { UserMenu } from "./user-menu";
import { MembersDialog } from "@/client/components/collaboration/members-dialog";
import { GlobalSearch } from "@/client/components/search/global-search";
import { MOD } from "@/client/lib/keys";

// The exporters load when the dialog first opens
const ExportDialog = lazy(() => import("@/client/components/export/export-dialog").then((m) => ({ default: m.ExportDialog })));

interface HeaderProps {
  storyName?: string;
  storyId?: string;
}

// Offline, writing keeps saving to this device; structure changes, AI, and
// invitations need a connection and aren't queued.
function ConnectionIndicator() {
  const online = useOnline();
  if (online) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex h-8 items-center gap-1.5 px-2 text-xs text-yellow-600 dark:text-yellow-500" role="status">
          <CloudOff className="h-4 w-4" />
          Offline
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>
          Writing is kept on this device and saves when you reconnect. Adding, moving, or deleting
          things, AI, and invitations need a connection.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

/** On a phone the story's actions share one menu */
function MoreMenu({ onExport, onMembers }: { onExport: () => void; onMembers: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="More">
          <EllipsisVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onExport}>
          <Download />
          Export to Ren'Py
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMembers}>
          <Users />
          Collaborators
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header({ storyName, storyId }: HeaderProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Stays mounted once opened, so it can animate closed
  const [exportUsed, setExportUsed] = useState(false);
  if (exportOpen && !exportUsed) setExportUsed(true);
  const [membersOpen, setMembersOpen] = useState(false);

  // Keyboard shortcut for search (Cmd/Ctrl + P)
  // Use capture phase to intercept before browser handles it
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "p") {
        e.preventDefault();
        e.stopPropagation();
        setSearchOpen(true);
      }
    };

    // Use capture: true to get the event before it bubbles
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  return (
    <>
      <header data-region="header" className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-12 items-center justify-between gap-2 px-2 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            {storyId ? (
              <>
                <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                  <Link to="/" aria-label="Back to your stories" title="Back to your stories">
                    <ChevronLeft className="h-4 w-4" />
                  </Link>
                </Button>
                <div data-region="header-title" className="flex min-w-0 items-center gap-2">
                  <BookOpen className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">{storyName}</span>
                </div>
              </>
            ) : (
              <Link to="/" data-region="header-title" className="flex items-center gap-2 hover:opacity-80">
                <BookOpen className="h-5 w-5" />
                <span className="font-semibold text-lg">RenTeller</span>
              </Link>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {/* Search Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-2 px-2"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Search"
                  aria-keyshortcuts={MOD === "⌘" ? "Meta+P" : "Control+P"}
                >
                  <Search className="h-4 w-4" />
                  <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                    {MOD}P
                  </kbd>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Search the story</p>
              </TooltipContent>
            </Tooltip>

            {storyId && (
              <div className="hidden items-center gap-1 md:flex">
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setExportOpen(true)} title="Export to Ren'Py">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="Collaborators"
                      onClick={() => setMembersOpen(true)}
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Collaborators</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
            {storyId && (
              <div className="md:hidden">
                <MoreMenu onExport={() => setExportOpen(true)} onMembers={() => setMembersOpen(true)} />
              </div>
            )}
            <ConnectionIndicator />
            <UserMenu />
          </div>
        </div>
      </header>

      <ShortcutsDialog />

      {/* Global Search Dialog */}
      <GlobalSearch
        storyId={storyId}
        open={searchOpen}
        onOpenChange={setSearchOpen}
      />

      {/* Export Dialog */}
      {storyId && exportUsed && (
        <Suspense fallback={null}>
          <ExportDialog
            storyId={storyId}
            open={exportOpen}
            onOpenChange={setExportOpen}
          />
        </Suspense>
      )}

      {storyId && (
        <MembersDialog
          storyId={storyId}
          storyName={storyName ?? "this story"}
          open={membersOpen}
          onOpenChange={setMembersOpen}
        />
      )}
    </>
  );
}
